import type mdast from 'mdast';
import { expandWhitespace } from '../textToHtml';
import { MarkdownParentBuffer } from './markdownParentBuffer';

/** Hoists leading and trailing whitespace before/after their style/emphasis containers so it's Markdown-compatible */
export class MarkdownStyleBuffer extends MarkdownParentBuffer<
  mdast.Emphasis | mdast.Strong | mdast.Delete,
  mdast.PhrasingContent
> {
  *flush(): Iterable<mdast.PhrasingContent> {
    const { parent } = this;
    const children = this.flushChildren();
    if (!children) {
      return;
    }

    const parts = collectSurroundingWhitespace(children);
    if (!parts) {
      return;
    }

    const { leadingWhitespace, flankedChildren, trailingWhitespace } = parts;

    // Keep leading whitespace outside the style container.
    if (leadingWhitespace.length > 0) {
      yield {
        type: 'text',
        value: expandWhitespace(leadingWhitespace),
      };
    }

    if (flankedChildren.length > 0) {
      yield {
        ...parent,
        children: flankedChildren,
      };
    }

    // Keep trailing whitespace outside the style container.
    if (trailingWhitespace.length > 0) {
      yield {
        type: 'text',
        value: expandWhitespace(trailingWhitespace),
      };
    }
  }
}

function collectSurroundingWhitespace(children: Iterable<mdast.PhrasingContent>) {
  const parts = collectSurroundingText(children);

  if (!parts) {
    return undefined;
  }

  const leadingMatch = parts.leadingText.match(/^(\s*)(.*)$/);
  const trailingMatch = parts.trailingText.match(/^(.*?)(\s*)$/);

  // This should never happen since those regular expressions should always match.
  if (!leadingMatch || !trailingMatch || leadingMatch.length < 3 || trailingMatch.length < 3) {
    return undefined;
  }

  const [, leadingWhitespace, leadingText] = leadingMatch;
  const [, trailingText, trailingWhitespace] = trailingMatch;

  // Handle the non-whitespace part of the leading text.
  const leadingTextChild: mdast.PhrasingContent | undefined =
    leadingText.length > 0
      ? {
          type: 'text',
          value: leadingText,
        }
      : undefined;

  // Handle the non-whitespace part of the trailing text.
  const trailingTextChild: mdast.PhrasingContent | undefined =
    trailingText.length > 0
      ? {
          type: 'text',
          value: trailingText,
        }
      : undefined;

  // Add the non-whitespace leading/trailing text to the children.
  const flankedChildren: mdast.PhrasingContent[] = [
    ...(leadingTextChild ? [leadingTextChild] : []),
    ...parts.flankedChildren,
    ...(trailingTextChild ? [trailingTextChild] : []),
  ];

  return { leadingWhitespace, flankedChildren, trailingWhitespace };
}

function collectSurroundingText(children: Iterable<mdast.PhrasingContent>) {
  // Break up the children into leading text, trailing text, and everything in between.
  let leadingText = '';
  let trailingText = '';
  const flankedChildren: Array<mdast.PhrasingContent> = [];

  // Buffer.
  let text = '';
  let isLeadingText = true;

  for (const child of children) {
    // Append text to our running buffer.
    if (child.type === 'text') {
      text += child.value;
      continue;
    }

    // We encountered a non-text child for the first time, flush our buffer as the leading text.
    if (isLeadingText) {
      leadingText = text;
      isLeadingText = false;
    }
    // We encountered a non-text child, flush our text buffer as a text child.
    else if (text.length > 0) {
      flankedChildren.push({
        type: 'text',
        value: text,
      });
    }
    text = '';

    flankedChildren.push(child);
  }

  if (isLeadingText) {
    // We did not encounter any non-text children, so extract non-whitespace text instead.
    const match = text.match(/^(\s*)(.+?)(\s*)$/);

    // We cannot emphasize only whitespace in Markdown.
    if (!match || match.length < 4) {
      return undefined;
    }

    const [, leadingWhitespace, flankedText, trailingWhitespace] = match;

    // Treat the non-whitespace text as a child, so we can process leading and trailing whitespace separately.
    leadingText = leadingWhitespace;
    flankedChildren.push({
      type: 'text',
      value: flankedText,
    });
    trailingText = trailingWhitespace;
  } else {
    // Flush our buffer as the trailing text.
    trailingText = text;
    text = '';
  }

  return { leadingText, flankedChildren, trailingText };
}
